// Uložený pohled: data se načtou znovu přes bránu s právy toho, kdo se dívá.
import { ArrowClockwiseIcon, ChatCircleIcon } from '@phosphor-icons/react';
import { Badge, Button, EmptyState, Spinner } from '@smworks-cz/ui-kit';
import { describePeriod, ROLE_LABEL, toolLabel, type MeDTO } from '@sam/shared';
import { WidgetCard, WidgetSkeleton } from '../components/widgets/WidgetCard';
import { keys, queryClient, useView, useViewData } from '../lib/queries';
import { navigate } from '../lib/router';
import { ApiError } from '../lib/api';

export function View({ id, me }: { id: string; me: MeDTO }) {
  const view = useView(id);
  const data = useViewData(id, !!view.data);
  if (view.error) {
    const notFound = view.error instanceof ApiError && view.error.status === 404;
    return (
      <div className="page-center">
        <EmptyState preset={notFound ? 'no-results' : 'error'} title={notFound ? 'Pohled neexistuje' : 'Pohled nejde načíst'} description={(view.error as Error).message} />
      </div>
    );
  }
  if (!view.data) return <div className="page-center"><Spinner /></div>;
  const v = view.data;
  const period = describePeriod(v.query.params);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: keys.viewData(id) });
  const d = data.data;

  return (
    <div className="page">
      <div className="view-h">
        <div>
          <h2>{v.title}</h2>
          <p className="muted small">
            {toolLabel(v.query.tool).done}
            {period && ` · období: ${period} (posouvá se)`}
          </p>
        </div>
        <div className="view-actions">
          <Badge label={`Zobrazeno podle práv: ${ROLE_LABEL[me.user.role]}`} variant="neutral" />
          <Button size="sm" variant="outline" icon={<ArrowClockwiseIcon size={14} />} onClick={refresh} loading={data.isFetching}>
            Obnovit
          </Button>
        </div>
      </div>
      {data.isLoading && <WidgetSkeleton title={v.title} />}
      {d && !d.ok && (
        <div className="lockbox">
          <EmptyState
            preset={d.denied ? 'no-permission' : 'error'}
            title={d.denied ? 'K tomuto pohledu nemáte oprávnění' : 'Data teď nejdou načíst'}
            description={d.error}
          />
        </div>
      )}
      {d?.ok && <WidgetCard spec={v.spec} dataset={d.dataset} />}
      <p className="muted small">
        Pohled se při každém otevření načte znovu přes bránu a zapíše se do záznamu přístupů. Nový pohled vytvoříte v chatu: požádejte Sama o graf nebo
        přehled a u widgetu klikněte na „Uložit jako pohled“.
      </p>
      <div>
        <Button size="sm" variant="secondary" icon={<ChatCircleIcon size={14} />} onClick={() => navigate('/')}>
          Zeptat se Sama
        </Button>
      </div>
    </div>
  );
}

/** Karty pohledů na úvodní stránce, s živými daty. Úzká karta (2 sloupce) je kompaktní, širší ukáže víc. */
export function ViewTile({ id, title, cols = 2, onOpen }: { id: string; title: string; cols?: number; onOpen?: () => void }) {
  const view = useView(id);
  const data = useViewData(id, !!view.data);
  if (!view.data || data.isLoading) return <WidgetSkeleton title={title} />;
  const d = data.data;
  if (!d?.ok)
    return (
      <button type="button" className="w-card compact clickable w-locked" onClick={onOpen}>
        <div className="w-head">
          <h3>{title}</h3>
        </div>
        <p className="small muted">{d?.denied ? 'Bez oprávnění' : 'Data nejdou načíst'}</p>
      </button>
    );
  return <WidgetCard spec={view.data.spec} dataset={d.dataset} compact={cols <= 2} maxRows={cols >= 5 ? 10 : 6} tile onOpen={onOpen} />;
}
